import { useCallback, useEffect, useState } from "react";
import { getMember } from "../api/members";
import { BASE_URL } from "../api/client";
import avatar from "../assets/images/none_profile.png";
import CloseIcon from "../assets/icons/close.png";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const ModalContainer = styled.div`
  position: fixed;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ModalContent = styled.div`
  width: 650px;
  height: 500px;
  max-width: calc(100% - 80px);
  background-color: white;
  border-radius: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: visible;
`;

const MemberDetailContainer = styled.div`
  background: #fff;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 80px);
  border-radius: 30px;
  display: flex;
  align-items: flex-start;
  transition: all 0.3s ease-in-out;
  position: relative;
  overflow: hidden;
  padding: 30px;
  gap: 30px;
`;

const MemberDetailProfileImageWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10px;

  img {
    width: 120px;
    height: 120px;
    object-fit: cover;
    object-position: top;
    border-radius: 50%;
    border: 3px solid #f0f0f0;
  }
`;

const MemberDetailInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
  height: 100%;
  overflow: hidden;
`;

const MemberInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 2px solid #f0f0f0;

  > h6{
    font-size: 1.4rem;
    line-height: 1.3;
    font-weight: 600;
    color: var(--primary-c);
    letter-spacing: 0.5px;
  }

  > h3{
    font-size: 2.4rem;
    font-weight: 700;
    color: #2c2c2c;
    line-height: 1.2;
    margin: 0;
  }
`;

const MemberDetailInfoEmailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.4rem;
  color: #666;
  padding-top: 6px;

  > p {
    margin: 0;
    word-break: break-all;
  }
`;

const MemberDetailInfoItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 10px;

    &:hover {
      background: #b0b0b0;
    }
  }
`;

const MemberDetailInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background-color: #fafafa;
  border-radius: 10px;
  border-left: 4px solid var(--primary-c);
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  > h6{
    font-size: 1.5rem;
    line-height: 1.2;
    font-weight: 700;
    color: var(--primary-c);
    margin: 0;
    letter-spacing: 0.3px;
  }
`;

const MemberDetailInfoItemContent = styled.div`
  font-size: 1.4rem;
  line-height: 1.7;
  color: #4a4a4a;
  min-height: 20px;

  > p{
    margin: 0;
    padding: 0;
    white-space: pre-wrap;
    word-break: keep-all;
  }
`;

const ConsultingButton = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 10px;
  background-color: var(--primary-c);
  transition: all 0.3s ease;
  color: #fff;
  font-size: 1.6rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  margin-top: auto;
  box-shadow: 0 4px 12px rgba(0, 137, 179, 0.2);

  &:hover {
    background-color: #0089b3;
    box-shadow: 0 6px 16px rgba(0, 137, 179, 0.3);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -20px;
  right: -20px;
  background-color: var(--primary-c);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(var(--primary-c), 0.3);

  &:hover {
    background-color: var(--primary-c-hover);
    box-shadow: 0 6px 16px rgba(var(--primary-c), 0.4);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
 `;
export default function MemberDetailModal({ onClose, memberId }) {
  const navigate = useNavigate();
  const [member, setMember] = useState([]);

  const fetchMember = useCallback(async () => {
    const data = await getMember(memberId);
    setMember(data.member);
  }, [memberId]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  // 모달이 열릴 때 배경 스크롤 방지
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleConsultingClick = () => {
    const params = new URLSearchParams();
    params.set("memberId", member.id);
    if (member.workplace) {
      params.set("branch", member.workplace);
    }
    navigate(`/consulting?${params.toString()}`);
  };

  const handleClose = () => {
    setTimeout(onClose, 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };

  const handleCloseKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClose();
    }
  };

  const handleConsultingKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleConsultingClick();
    }
  };

  return (
    <ModalContainer
      onClick={handleClose}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-name"
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <MemberDetailContainer>
          {member.profile && (
            <MemberDetailProfileImageWrapper>
              <img
                src={`${BASE_URL}/image/${member.profile}`}
                alt={member.name || "프로필 이미지"}
              />
            </MemberDetailProfileImageWrapper>
          )}
          <MemberDetailInfoWrapper>
            <MemberInfoContainer>
              <h6>{member.role || ""}</h6>
              <h3 id="member-name">{member.name || ""}</h3>
              <MemberDetailInfoEmailItem>
                <p>{member.email || ""}</p>
              </MemberDetailInfoEmailItem>
            </MemberInfoContainer>
            <MemberDetailInfoItemsWrapper>
              <MemberDetailInfoItem>
                <h6>서비스</h6>
                <MemberDetailInfoItemContent>
                  {member.service ? (
                    <p>{member.service}</p>
                  ) : (
                    <p style={{ color: "#999", fontStyle: "italic" }}>정보가 없습니다.</p>
                  )}
                </MemberDetailInfoItemContent>
              </MemberDetailInfoItem>
              <MemberDetailInfoItem>
                <h6>학력</h6>
                <MemberDetailInfoItemContent>
                  {member.education ? (
                    <p>{member.education}</p>
                  ) : (
                    <p style={{ color: "#999", fontStyle: "italic" }}>정보가 없습니다.</p>
                  )}
                </MemberDetailInfoItemContent>
              </MemberDetailInfoItem>
              <MemberDetailInfoItem>
                <h6>경력</h6>
                <MemberDetailInfoItemContent>
                  {member.career ? (
                    <p>{member.career}</p>
                  ) : (
                    <p style={{ color: "#999", fontStyle: "italic" }}>정보가 없습니다.</p>
                  )}
                </MemberDetailInfoItemContent>
              </MemberDetailInfoItem>
            </MemberDetailInfoItemsWrapper>
            <ConsultingButton
              onClick={handleConsultingClick}
              onKeyDown={handleConsultingKeyDown}
              tabIndex={0}
              aria-label="상담 신청하기"
            >
              상담신청하기
            </ConsultingButton>
          </MemberDetailInfoWrapper>
        </MemberDetailContainer>
        <CloseButton
          onClick={handleClose}
          onKeyDown={handleCloseKeyDown}
          tabIndex={0}
          aria-label="모달 닫기"
        >
          <img src={CloseIcon} alt="닫기" />
        </CloseButton>
      </ModalContent>
    </ModalContainer>
  );
}